import { ONE_MINUTE } from '@air/utilities';

import { UNTITLED_AUTOMATION_LABEL } from '~/components/Automations/detail/automationDetailFields';
import {
  formatAutomationLastRun,
  formatAutomationScheduleSentence,
} from '~/components/Automations/utils/automationSchedule';
import { splitPillMonikers } from '~/components/v2/components/workspace/workspace-agent-panel/utils/splitPillMonikers';
import { WORKSPACE_AGENT_SESSION_ROW_PREVIEW_MAX_LENGTH } from '~/components/v2/components/workspace/workspace-agent-panel/workspaceAgentPanelConstants';
import { type AutomationRun } from '~/hooks/queries/automations/usePrivateAutomationRunsList';
import { type Automation } from '~/hooks/queries/automations/usePrivateAutomationsList';

const WORKSPACE_AGENT_AUTOMATION_DISPLAY_NAME_MAX_LENGTH = 80;

const AUTOMATION_LAST_RUN_JUST_NOW_THRESHOLD_MS = ONE_MINUTE;

export const formatWorkspaceAgentAutomationDisplayName = (automation: Pick<Automation, 'title'>) => {
  const plain = automation.title?.trim() ?? '';
  const display = plain.length > 0 ? plain : UNTITLED_AUTOMATION_LABEL;

  if (display.length <= WORKSPACE_AGENT_AUTOMATION_DISPLAY_NAME_MAX_LENGTH) {
    return display;
  }

  return `${display.slice(0, WORKSPACE_AGENT_AUTOMATION_DISPLAY_NAME_MAX_LENGTH - 1).trimEnd()}…`;
};

export const formatWorkspaceAgentAutomationLastRunLabel = ({
  lastRunAt,
  now,
  timeZone,
}: {
  lastRunAt: string | null | undefined;
  now: Date;
  timeZone: string;
}) => {
  if (!lastRunAt) {
    return 'Last run —';
  }

  const startedAt = new Date(lastRunAt);
  const elapsedMs = now.getTime() - startedAt.getTime();

  if (elapsedMs >= 0 && elapsedMs < AUTOMATION_LAST_RUN_JUST_NOW_THRESHOLD_MS) {
    return 'Last run Just now';
  }

  return formatAutomationLastRun({ lastRunAt, now, timeZone });
};

export const formatWorkspaceAgentAutomationRunsSubtitle = ({
  cronSchedule,
  now,
  runCount,
  timeZone,
}: {
  cronSchedule: string;
  now: Date;
  runCount: number;
  timeZone: string;
}) => {
  const schedule = formatAutomationScheduleSentence({ cronSchedule, now, timeZone });
  const runsLabel = runCount === 1 ? '1 run' : `${runCount} runs`;

  return `${schedule} · ${runsLabel}`;
};

export const getAutomationRunLastMessageContent = (run: AutomationRun): string | null => {
  if (!('lastMessage' in run)) {
    return null;
  }

  const { lastMessage } = run;

  if (lastMessage === null || lastMessage === undefined) {
    return null;
  }

  if (typeof lastMessage !== 'object' || !('content' in lastMessage)) {
    return null;
  }

  const { content } = lastMessage;

  if (content === null || typeof content === 'string') {
    return content;
  }

  return null;
};

export const prepareWorkspaceAgentAutomationRunPreviewText = (content: string | null | undefined) => {
  if (!content) {
    return null;
  }

  const trimmed = content.trim();

  if (trimmed.length === 0) {
    return null;
  }

  let textBeforeFirstPill = '';

  for (const segment of splitPillMonikers(trimmed)) {
    if (segment.type === 'pill' || segment.type === 'unknownMoniker') {
      break;
    }

    if (segment.type === 'text') {
      textBeforeFirstPill += segment.value;
    }
  }

  const normalized = textBeforeFirstPill.replace(/\s+/gu, ' ').trim();

  if (normalized.length === 0) {
    return null;
  }

  if (normalized.length <= WORKSPACE_AGENT_SESSION_ROW_PREVIEW_MAX_LENGTH) {
    return normalized;
  }

  return `${normalized.slice(0, WORKSPACE_AGENT_SESSION_ROW_PREVIEW_MAX_LENGTH - 1).trimEnd()}…`;
};
